import ContractVersionContents, {
  type PopupData,
} from "~/features/contracts/components/AddToCartPopup/ContractVersionContents";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AtcProductInfo from "~/features/addToCart/components/v2/AtcProductInfo/AtcProductInfo";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import Modal from "~/common/components/Modal";
import { IconType } from "~/common/components/icon/IconType";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";
import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { type ProductData } from "~/domains/product/types/ProductData";
import { Button, Stack } from "~/common/mui";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { ContractAddToCartPopupStates } from "~/features/contracts/components/AddToCartPopup/ContractAddToCartPopupStates";
import { Alert } from "~/common/mui/Alert/Alert";
import AtcProductPriceInfo from "~/features/addToCart/components/v2/AtcProductPriceInfo/AtcProductPriceInfo";

interface AddToCartPopupProps {
  product: ProductData;
  contractDetails: ContractDetailsResponse;
  contractStatus: ContractAddToCartPopupStates;
  onClose: () => void;
  onAdd: (data: PopupData) => void;
}

const NewAddToCartPopup = ({ product, contractDetails, contractStatus, onClose, onAdd }: AddToCartPopupProps) => {
  const { t } = useTranslation();

  const packageSize = contractDetails.packSize;
  const packageCode = contractDetails.packCode;

  //AtcProductInfo uses FormContext to get pack information, so we pass down the needed value on the FormProvider
  const methods = useForm({
    defaultValues: { selectedPack: { packSize: packageSize }, selectedPackCode: packageCode },
  });

  const price = contractDetails.nettoPrice;

  const netPrice: NetPriceData = {
    ...price,
    unit: 1, // I guess contracts have always unit of 1?
    baseValue: price.value, // With no surcharge, base value === value
    surcharge: 0, // Can contracts have surcharge?
    isSpecial: false, // Can contracts have special price?
    isSubscription: !product.quantityVisible,
  };

  const getRightSideClass = () => {
    switch (contractStatus) {
      case ContractAddToCartPopupStates.LOADING:
      case ContractAddToCartPopupStates.SENDING:
        return "scha__atc-popup--column-spinner";
      default:
        return "scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging";
    }
  };

  const getContents = () => {
    switch (contractStatus) {
      case ContractAddToCartPopupStates.LOADING:
      case ContractAddToCartPopupStates.SENDING:
        return <CircleSpinner />;
      case ContractAddToCartPopupStates.SEND_SUCCESS:
        return (
          <Alert className="mb-30" severity={"info"}>
            {t(`addToCartPopup.add.success`)}
          </Alert>
        );
      case ContractAddToCartPopupStates.ERROR_LOADING:
        return (
          <Alert className="mb-30" severity={"info"}>
            {t(`hybrisAjaxError`)}
          </Alert>
        );
      case ContractAddToCartPopupStates.SEND_FAILURE:
        return (
          <Alert className="mb-30" severity={"error"}>
            {t(`addToCartPopup.add.error`)}
          </Alert>
        );
      default:
        return undefined;
    }
  };

  const getButtons = () => {
    const noButtonStates = [
      ContractAddToCartPopupStates.LOADING,
      ContractAddToCartPopupStates.SENDING,
      ContractAddToCartPopupStates.LOADED,
    ];

    if (noButtonStates.indexOf(contractStatus) !== -1) {
      return undefined;
    }

    const disabledOkStates = [ContractAddToCartPopupStates.ERROR_LOADING, ContractAddToCartPopupStates.SEND_FAILURE];

    const okDisabled = disabledOkStates.indexOf(contractStatus) !== -1 || window.SCHA.config.disableAllWS;
    const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

    return (
      <Stack direction={"row"} gap={1}>
        <Button data-test-id={"add-to-cart-btn-cancel"} variant={"text"} onClick={onClose}>
          {t("addToCartPopup.backToStore")}
        </Button>
        {showAddToCart && (
          <Button
            data-test-id={"add-to-cart-btn-ok"}
            href={CART_PAGE}
            startIcon={<DefaultButtonIcon type={IconType.Cart} />}
            disabled={okDisabled}
          >
            {t("addToCartPopup.checkoutBtn")}
          </Button>
        )}
      </Stack>
    );
  };

  if (contractStatus !== ContractAddToCartPopupStates.LOADED) {
    return (
      <Modal onClose={onClose} footerChildren={getButtons()}>
        <div className="scha__atc-popup scha__atc-popup-continer">
          <div className="scha__atc-popup--column-info">
            <AtcProductInfo descriptor={fakeDescriptorFromProduct(product)} variant="CONTRACT" />
          </div>
          <div className={getRightSideClass()}>
            <div className="w-100">{getContents()}</div>
            <div>
              <AtcProductPriceInfo
                descriptor={fakeDescriptorFromProduct(product)}
                quantity={null}
                price={netPrice}
                priceUnit={1}
                disabled={true}
                priceReference={""}
                variant="CONTRACT"
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <FormProvider {...methods}>
      <form>
        <ContractVersionContents
          packageCode={packageCode.toString()}
          packageSize={packageSize}
          onCancel={onClose}
          product={product}
          onOk={onAdd}
          contractDetails={contractDetails}
        />
      </form>
    </FormProvider>
  );
};

export default NewAddToCartPopup;
